import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sofa, Ship, Globe, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const InteractiveDivisionSelector = () => {
  const [selectedDivision, setSelectedDivision] = useState(null);

  const divisions = [
    {
      id: 'furniture',
      title: 'Furniture & Interiors',
      icon: Sofa,
      tagline: 'Custom Furniture. Turnkey Interiors.',
      description: 'From modular kitchens to complete office fitouts, we design and manufacture premium furniture with precision engineering.',
      features: ['Modular Kitchens', 'Wardrobes', 'Office Furniture', 'Turnkey Interiors'],
      image: 'https://images.unsplash.com/photo-1751591148854-49c2835dfaa1?crop=entropy&cs=srgb&fm=jpg&q=85',
      link: '/furniture',
      color: 'from-slate-900 to-slate-700'
    },
    {
      id: 'export',
      title: 'Export Division',
      icon: Ship,
      tagline: 'Global Trade. Sustainable Products.',
      description: 'Export premium coconut fibre and coco peat to 25+ countries with world-class logistics and quality certifications.',
      features: ['Coconut Fibre', 'Coco Peat', 'Global Shipping', 'ISO Certified'],
      image: 'https://images.pexels.com/photos/9694390/pexels-photo-9694390.jpeg?auto=compress&cs=tinysrgb&w=1260',
      link: '/export',
      color: 'from-blue-900 to-blue-700'
    },
    {
      id: 'sourcing',
      title: 'Global Sourcing',
      icon: Globe,
      tagline: 'China Partnerships. Quality Procurement.',
      description: 'Strategic sourcing from trusted Chinese manufacturers with rigorous quality inspection and seamless logistics.',
      features: ['China Sourcing', 'Quality Inspection', 'Procurement', 'Shipping Management'],
      image: 'https://images.pexels.com/photos/14805033/pexels-photo-14805033.jpeg?auto=compress&cs=tinysrgb&w=1260',
      link: '/global-sourcing',
      color: 'from-bronze-600 to-bronze-500'
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="interactive-division-selector">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Choose Your Path</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
              How Can We Serve You?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Select a division to explore our capabilities and discover how LMR IMPEX can bring your vision to life.
            </p>
          </motion.div>
        </div>

        {/* Division Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {divisions.map((division, index) => (
            <motion.div
              key={division.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setSelectedDivision(selectedDivision === division.id ? null : division.id)}
              className={`group cursor-pointer border-2 transition-all duration-500 overflow-hidden ${
                selectedDivision === division.id
                  ? 'border-slate-900 shadow-xl scale-105'
                  : 'border-slate-200 hover:border-slate-300 hover:shadow-lg'
              }`}
              data-testid={`division-card-${division.id}`}
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={division.image} 
                  alt={division.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${division.color} opacity-60`} />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-6">
                  <division.icon className="w-12 h-12 mb-4" />
                  <h3 className="text-2xl font-semibold mb-2">{division.title}</h3>
                  <p className="text-sm font-medium opacity-90">{division.tagline}</p>
                </div>
              </div>
              <div className="p-6 bg-white">
                <p className="text-muted-foreground text-sm mb-4">{division.description}</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {division.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-bronze-500 flex-shrink-0" />
                      <span className="text-xs text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={division.link}
                  className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-slate-900 hover:text-bronze-500 transition-colors group"
                  data-testid={`division-explore-${division.id}`}
                >
                  Explore
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Expanded Details */}
        <AnimatePresence>
          {selectedDivision && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-slate-50 p-8 border border-slate-200"
              data-testid="division-expanded-content"
            >
              {divisions.map((division) => {
                if (division.id !== selectedDivision) return null;
                return (
                  <div key={division.id} className="text-center">
                    <h3 className="text-2xl font-semibold mb-4">Ready to get started with {division.title}?</h3>
                    <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                      Our team is ready to discuss your requirements and provide a customized solution.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                      <Link
                        to={division.link}
                        className="inline-flex items-center bg-slate-900 text-white hover:bg-slate-800 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all"
                      >
                        View {division.title}
                      </Link>
                      <Link
                        to="/contact"
                        className="inline-flex items-center border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all"
                      >
                        Get in Touch
                      </Link>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default InteractiveDivisionSelector;
